import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Input, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { ListPager, ListSearchBox, ListTable, ListToolbar } from '../../../components/list';
import { SEARCH_DICTIONARY_SYNONYM_CREATE_URL_PATH } from '../../../constants/service/common/urlConstants';
import { SynonymRule } from '../../../lib/api/searchDictionary';
import { searchDictionaryQuery } from '../queries';
import { useSynonymListState } from '../hooks/useSynonymListState';
import { ListReturnState, ruleDetailUrl } from '../utils/routes';
import { SynonymQuickEditModal } from './SynonymQuickEditModal';
import { TermTags } from './TermTags';

type SynonymListPanelProps = {
  canRead: boolean;
  /** 규칙 등록 — 등록 화면이 검증·미리보기를 불러 create + read 를 함께 요구한다 */
  canCreate: boolean;
  canUpdate: boolean;
};

const LIVE_FIELD_LABEL: Record<string, string> = {
  productNameKorean: '상품명',
  storeNameKorean: '브랜드명',
};

/**
 * 동의어 목록 (S3-D) — 줄을 누르면 상세, 오른쪽 「빠른 편집」 은 모달.
 * 945줄 규모라 검색·경고 거르기·페이지가 필요하다. 상태는 전부 URL 에 있다(링크 공유·뒤로가기).
 */
export const SynonymListPanel = (props: SynonymListPanelProps): React.JSX.Element => {
  const { canRead, canCreate, canUpdate } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const listState = useSynonymListState();
  const [editing, setEditing] = useState<SynonymRule | null>(null);

  const { data, isFetching, isPreviousData, isError, refetch } = useQuery({
    ...searchDictionaryQuery.synonyms({
      keyword: listState.query.keyword || undefined,
      warnedOnly: listState.query.warnedOnly || undefined,
      page: listState.page,
      pageSize: listState.pageSize,
    }),
    keepPreviousData: true,
  });
  const totalCount = data?.totalCount ?? 0;

  // 범위를 넘은 페이지는 지금 조건의 응답으로만 되돌린다(프로모션 목록과 같은 이유)
  useEffect(() => {
    if (!isFetching && !isPreviousData && data) listState.clampPage(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isPreviousData, data, totalCount, listState.page]);

  /** 지금 보던 목록 URL 을 들려 보낸다 — 상세의 「목록」 이 필터·페이지를 그대로 되살린다 */
  const listReturn: ListReturnState = { listUrl: `${location.pathname}${location.search}` };
  const openDetail = (ruleId: string): void => navigate(ruleDetailUrl(ruleId), { state: listReturn });

  const liveFields = (data?.liveFields ?? []).map((field) => LIVE_FIELD_LABEL[field] ?? field).join(' · ');

  const columns: ColumnsType<SynonymRule> = [
    { title: '번호', dataIndex: 'no', width: 72, align: 'center' },
    {
      title: '토큰',
      dataIndex: 'terms',
      className: 'token-cell',
      render: (_, rule) => (
        <div className="term-tags-row">
          <TermTags terms={rule.terms} warnings={rule.warnings} />
          {rule.isMapping && <Tag color="blue">매핑</Tag>}
          {/* 줄을 누르면 상세로 간다는 걸 마우스를 올렸을 때 알려 준다. 키보드로는 Tab 으로 이 링크에 닿는다 */}
          {canRead && (
            <a
              className="row-open-hint"
              href={ruleDetailUrl(rule.id)}
              aria-label={`${rule.no}번 줄 상세 보기`}
              onClick={(event) => {
                // 행 클릭과 겹쳐 두 번 이동하지 않게
                event.stopPropagation();
                // Ctrl/Cmd/Shift/Alt+클릭은 브라우저에 맡긴다(새 탭 · 새 창). 새 탭엔 목록 조건이 없어 「목록」 은 첫 화면으로 간다
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                openDetail(rule.id);
              }}
            >
              상세 보기 ›
            </a>
          )}
        </div>
      ),
    },
    {
      title: '경고',
      dataIndex: 'warnings',
      width: 80,
      align: 'center',
      render: (_, rule) => (rule.warnings.length ? <Tag color="orange">{rule.warnings.length}건</Tag> : null),
    },
    // 빠른 편집은 검증·미리보기(read)를 부른다 — 목록 권한만 있으면 감춘다
    ...(!canRead
      ? []
      : [
          {
            title: '',
            width: 110,
            align: 'center' as const,
            render: (_: unknown, rule: SynonymRule) => (
              <Button
                size="small"
                onClick={(event) => {
                  // 행 클릭(상세 이동)으로 번지지 않게
                  event.stopPropagation();
                  setEditing(rule);
                }}
              >
                빠른 편집
              </Button>
            ),
          },
        ]),
  ];

  return (
    <div className="settings-stack">
      <Alert
        className="settings-inline-alert"
        type="info"
        showIcon
        message={`동의어는 저장하면 바로 검색에 반영됩니다${liveFields ? ` (${liveFields} 검색)` : ''}. 재색인은 필요 없습니다.`}
        description={
          <>
            태그·옵션·카테고리명 검색에는 아직 반영되지 않습니다. 같은 뜻의 말을 한 줄에 쉼표로 묶습니다.
            {/* 검색 조건과 무관한 사전 전체 규모 — 검색 칸 옆에 두면 조회 건수와 헷갈린다 */}
            {data && (
              <>
                {' '}
                지금 사전은 {data.setCount.toLocaleString()}줄이고, 그중 경고가 있는 줄은{' '}
                {data.warnedCount.toLocaleString()}줄입니다.
              </>
            )}
          </>
        }
      />

      <ListSearchBox onSearch={listState.search} onClear={listState.reset}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            aria-label="토큰 검색"
            placeholder="토큰으로 찾기 (예: 팬던트)"
            style={{ width: 280 }}
            value={listState.draft.keyword}
            onChange={(event) => listState.setDraft({ ...listState.draft, keyword: event.target.value })}
            onPressEnter={listState.search}
            allowClear
          />
          <Checkbox
            checked={listState.draft.warnedOnly}
            onChange={(event) => listState.setDraft({ ...listState.draft, warnedOnly: event.target.checked })}
          >
            경고 있는 줄만
          </Checkbox>
        </div>
      </ListSearchBox>

      {isError ? (
        <Alert
          type="error"
          showIcon
          message="동의어를 불러오지 못했습니다."
          action={<Button onClick={() => refetch()}>다시 시도</Button>}
        />
      ) : (
        <div className="synonym-table">
          <ListToolbar
            totalCount={totalCount}
            onClickCreate={
              canCreate ? () => navigate(SEARCH_DICTIONARY_SYNONYM_CREATE_URL_PATH, { state: listReturn }) : undefined
            }
            createText="+ 새 규칙"
          />
          <ListTable<SynonymRule>
            rowKey="id"
            columns={columns}
            dataSource={data?.items ?? []}
            isLoading={isFetching}
            onClickRow={canRead ? (rule) => openDetail(rule.id) : undefined}
            emptyText="조건에 맞는 동의어가 없습니다."
          />
          <ListPager
            page={listState.page}
            pageSize={listState.pageSize}
            totalCount={totalCount}
            onChange={listState.setPage}
          />
        </div>
      )}

      {editing && (
        <SynonymQuickEditModal
          rule={editing}
          canUpdate={canUpdate}
          onClose={() => setEditing(null)}
          onOpenDetail={(ruleId) => {
            setEditing(null);
            openDetail(ruleId);
          }}
        />
      )}
    </div>
  );
};
